import { useMemo } from "react";
import { Slider } from "@/components/ui/slider";
import { CLP } from "@/lib/format";
import { X, Check, Star, Sparkles, BrushCleaning } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export const PRICE_MAX = 10000000;
const LEVELS = ["Principiante", "Intermedio", "Avanzado"];
const HORARIOS = [
  { value: "manana", label: "Mañana", hint: "Antes de las 12 pm" },
  { value: "tarde", label: "Tarde", hint: "Entre 12 pm y 6 pm" },
  { value: "noche", label: "Noche", hint: "Después de las 6 pm" },
];
export const SLOTS = HORARIOS.map((h) => h.value);

function Pill({ label, active, onClick, testId, icon }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} aria-pressed={active}
      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm transition-colors ${active ? "bg-primary border-primary text-primary-foreground font-medium" : "border-border hover:border-primary/50 hover:bg-primary/5"}`}>
      {icon}{label}
    </button>
  );
}

function FieldLabel({ children }) {
  return <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{children}</p>;
}

// Fila de opción con check activo en el color de marca (primary).
export function CheckRow({ label, active, onClick, testId, badge }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} aria-pressed={active}
      className={`group flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm border text-left transition-colors ${active ? "bg-primary/10 border-primary/40" : "border-transparent hover:bg-accent/50"}`}>
      <span className={`grid place-items-center w-4 h-4 rounded border shrink-0 transition-colors ${active ? "bg-primary border-primary text-primary-foreground" : "border-border"}`}>
        {active && <Check className="w-3 h-3" />}
      </span>
      <span className={`flex-1 truncate ${active ? "font-medium text-foreground" : ""}`}>{label}</span>
      {badge}
    </button>
  );
}

export function ExploreFilters({
  categories, cats, toggleCat, clearCats, prefs = [],
  level, setLevel, maxPrice, setMaxPrice, minPrice = 0, setMinPrice, priceBounds, availability, setAvailability,
  onClear, activeCount, onCollapse, onEditPrefs,
}) {
  const ordered = useMemo(() => {
    const front = categories.filter((c) => prefs.includes(c) || cats.includes(c));
    return [...front, ...categories.filter((c) => !front.includes(c))];
  }, [categories, prefs, cats]);
  const toggleSlot = (s) => setAvailability(availability.includes(s) ? availability.filter((x) => x !== s) : [...availability, s]);

  return (
    <div className="space-y-6" data-testid="explore-filters">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-base">Filtros</h3>
        <TooltipProvider delayDuration={150}>
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={activeCount === 0 ? 0 : -1}>
                <button type="button" onClick={onClear} disabled={activeCount === 0} aria-label="Limpiar filtros" data-testid="clear-filters-btn"
                  className="grid place-items-center w-8 h-8 rounded-full text-primary hover:bg-primary/10 transition-colors disabled:opacity-40 disabled:pointer-events-none">
                  <BrushCleaning className="w-[18px] h-[18px]" />
                </button>
              </span>
            </TooltipTrigger>
            <TooltipContent>Limpiar filtros</TooltipContent>
          </Tooltip>
          {onCollapse && (
            <button onClick={onCollapse} data-testid="collapse-sidebar-btn" aria-label="Cerrar filtros"
              className="grid place-items-center w-8 h-8 rounded-full text-muted-foreground hover:bg-primary hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        </TooltipProvider>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <FieldLabel>Experiencia Taller</FieldLabel>
          {cats.length > 0 && (
            <button onClick={clearCats} className="text-[11px] font-medium text-primary hover:underline" data-testid="filter-cat-all">
              Ver todas
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="filter-categories">
          {ordered.map((c) => (
            <Pill key={c} label={c} active={cats.includes(c)} onClick={() => toggleCat(c)} testId={`filter-cat-${c}`}
              icon={prefs.includes(c) ? <Star className="w-3 h-3 fill-current" data-testid={`interest-badge-${c}`} /> : null} />
          ))}
        </div>
        {onEditPrefs && (
          <button onClick={onEditPrefs} data-testid="edit-prefs-btn"
            className="mt-2 flex items-center gap-1 text-xs font-medium text-primary hover:underline">
            <Sparkles className="w-3.5 h-3.5" />Editar mis gustos
          </button>
        )}
      </div>

      <div>
        <FieldLabel>Ideal para</FieldLabel>
        <div className="flex flex-wrap gap-1.5" data-testid="filter-level">
          <Pill label="Todos" active={level === "all"} onClick={() => setLevel("all")} testId="filter-level-all" />
          {LEVELS.map((l) => (
            <Pill key={l} label={l} active={level === l} onClick={() => setLevel(l)} testId={`filter-level-${l}`} />
          ))}
        </div>
      </div>

      <div>
        <FieldLabel>Horario</FieldLabel>
        <div className="flex flex-col gap-1" data-testid="filter-availability">
          {HORARIOS.map((h) => (
            <CheckRow key={h.value} active={availability.includes(h.value)} onClick={() => toggleSlot(h.value)} testId={`filter-availability-${h.value}`}
              label={<span className="flex flex-col leading-tight"><span>{h.label}</span><span className="text-xs text-muted-foreground font-normal">{h.hint}</span></span>} />
          ))}
        </div>
      </div>

      {/* Precio */}
      {priceBounds && priceBounds.max > priceBounds.min && (() => {
        const lo = Math.max(minPrice, priceBounds.min);
        const hi = Math.min(maxPrice, priceBounds.max);
        const step = priceBounds.max - priceBounds.min > 50000 ? 5000 : 1000;
        return (
          <div>
            <FieldLabel>Rango de precio</FieldLabel>
            <Slider value={[lo, hi]} min={priceBounds.min} max={priceBounds.max} step={step} minStepsBetweenThumbs={0}
              onValueChange={([a, b]) => { setMinPrice(a <= priceBounds.min ? 0 : a); setMaxPrice(b >= priceBounds.max ? PRICE_MAX : b); }}
              data-testid="price-slider" className="mt-1" />
            <div className="flex justify-between text-xs text-muted-foreground mt-2">
              <span data-testid="price-min-value"><span className="block text-[10px] uppercase tracking-wide">Mínimo</span><span className="font-semibold text-foreground">{CLP(lo)}</span></span>
              <span className="text-right" data-testid="price-value"><span className="block text-[10px] uppercase tracking-wide">Máximo</span><span className="font-semibold text-foreground">{CLP(hi)}</span></span>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
